import { Color, Spacing } from '@persona-id/design-tokens';
import { PlusIcon } from '@persona-id/design-tokens/icons';
import { omit } from 'lodash/fp';
import { useCallback, useMemo, useState, VFC } from 'react';
import { BORDER_RADIUSES } from 'shared/styles/variables';
import styled from 'styled-components';

import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import ReactSelect from 'dashboard/components/ReactSelect';
import ExpressionDisplay from 'dashboard/components/WorkflowControlPanel/StepConfigAction/ExpressionEditor/Display';
import { REACT_SELECT_STYLES } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/ExpressionEditor/TermConfig';
import VariableEditor from 'dashboard/components/WorkflowControlPanel/StepConfigAction/ExpressionEditor/VariableEditor';
import {
  DEFAULT_EXPRESSION,
  getExpressionRuleParts,
  RuleValueTypes,
} from 'dashboard/lib/workflows';
import { WorkflowError, WorkflowErrorType } from 'dashboard/models/workflow';
import { VariableConfig } from 'dashboard/models/workflow-version';
import Button from 'design-system/components/Button';
import { JsonSchema, JsonSchemaTypes } from 'lib/json-schema';
import { generateWorkflowToken } from 'lib/workflows';

const ALPHABET = Array.from(Array(26))
  .map((_, i) => i + 65)
  .map((x) => String.fromCharCode(x).toLowerCase());

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${Spacing[2]};
`;

const Well = styled.div<{ hasError: boolean }>`
  border-color: ${({ theme, hasError }) =>
    hasError ? theme.color.variants.danger.default : Color.CoreSlate50};
  border-width: 2px;
  border-style: solid;
  margin-bottom: ${Spacing[2]};
  padding: ${Spacing[4]};
  background-color: ${Color.CoreSlate50};
  border-radius: ${BORDER_RADIUSES.rounded};
`;

const MdSelectContainer = styled.div`
  width: ${({ theme }) => theme.element.width.md};
`;

interface EditorProps {
  errors: WorkflowError[];
  jsonSchema: JsonSchema;
  returnVariableId: string;
  tokensToDisplayValue: Record<string, string>;
  variables: VariableConfig[];
  deleteVariable: (index: number) => void;
  setReturnVariableId: (val: string) => void;
  setVariables: (val: VariableConfig[]) => void;
}

const Editor: VFC<EditorProps> = ({
  errors,
  jsonSchema,
  returnVariableId,
  tokensToDisplayValue,
  variables,
  deleteVariable,
  setReturnVariableId,
  setVariables,
}) => {
  const [expandedStates, setExpandedStates] = useState(
    variables.map((_, i) => i === variables.length - 1),
  );

  const isInvalidFieldName = useCallback(
    (name: string) =>
      name.length === 0 || variables.filter((entry) => entry.name === name).length > 1,
    [variables],
  );

  const nameErrors = useMemo(
    () => variables.map((entry) => isInvalidFieldName(entry.name)),
    [variables, isInvalidFieldName],
  );

  const { variableSchema, varTokensToDisplayValue } = useMemo(() => {
    const variableSchema: JsonSchema = {
      type: JsonSchemaTypes.Object,
      properties: {},
    };
    const varTokensToDisplayValue: Record<string, string> = {};

    variables.forEach(({ id, type, name }) => {
      if (name) {
        variableSchema.properties[name] = { type };
        varTokensToDisplayValue[id] = name;
      }
    });
    return {
      variableSchema,
      varTokensToDisplayValue,
    };
  }, [variables]);

  const variableOptions = variables.map((variable) => ({
    label: variable.name,
    value: variable.id,
  }));

  const checkIfReferenced = useCallback(
    (index: number) => {
      const currentVariableId = variables[index].id;
      const otherVariables = [...variables.slice(0, index), ...variables.slice(index + 1)];
      return otherVariables.some((internalVariable) => {
        const { valueLeftConfig, valueRightConfig, valueDefaultConfig } = getExpressionRuleParts(
          internalVariable.value,
        );
        return (
          (valueLeftConfig?.type === RuleValueTypes.Variable &&
            valueLeftConfig?.paths[0] === currentVariableId) ||
          (valueRightConfig?.type === RuleValueTypes.Variable &&
            valueRightConfig?.paths[0] === currentVariableId) ||
          (valueDefaultConfig?.type === RuleValueTypes.Variable &&
            valueDefaultConfig?.paths[0] === currentVariableId)
        );
      });
    },
    [variables],
  );

  return (
    <div aria-label='Expression editor'>
      <FormControlLabel htmlFor='expression-editor__return-input'>Return variable</FormControlLabel>
      <Row>
        <MdSelectContainer>
          <ReactSelect
            id='expression-editor__return'
            aria-label='Select return variable'
            value={returnVariableId}
            options={variableOptions}
            styles={REACT_SELECT_STYLES}
            isSearchable={false}
            onChange={setReturnVariableId}
          />
        </MdSelectContainer>
        <Button
          onClick={() => {
            const index = variables.length % ALPHABET.length;
            const repeat = Math.floor(variables.length / ALPHABET.length) + 1;
            const variableName = ALPHABET[index].repeat(repeat);
            setExpandedStates([...variables.map(() => false), true]);
            setVariables([
              ...variables,
              {
                id: generateWorkflowToken(),
                type: JsonSchemaTypes.Number,
                name: variableName,
                value: DEFAULT_EXPRESSION,
              },
            ]);
          }}
          id='expression-editor__add-variable'
          data-test='expression-editor__add-variable'
          type='button'
          variant='basic'
          width='auto'
          prefixIcon={<PlusIcon />}
        >
          Add variable
        </Button>
      </Row>
      <ExpressionDisplay
        variables={variables}
        tokensToDisplayValue={{
          ...tokensToDisplayValue,
          ...varTokensToDisplayValue,
        }}
        returnVariableId={returnVariableId}
      />
      {variables.map((variable, i) => {
        const variableErrors = errors.filter((error) =>
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          error.type === WorkflowErrorType.Run ? false : error.field.includes(variable.id),
        );
        const isReferenced = checkIfReferenced(i);
        return (
          <Well key={i} hasError={!!variableErrors.length}>
            <VariableEditor
              deletable={!isReferenced && variables.length > 1}
              errors={variableErrors}
              jsonSchema={jsonSchema}
              tokensToDisplayValue={tokensToDisplayValue}
              expanded={expandedStates[i]}
              isReferenced={isReferenced}
              toggleExpanded={() =>
                setExpandedStates((currentStates) => [
                  ...currentStates.slice(0, i),
                  !currentStates[i],
                  ...currentStates.slice(i + 1),
                ])
              }
              variable={variable}
              nameError={nameErrors[i]}
              variableSchema={omit(`properties.${variable.name}`, variableSchema) as JsonSchema}
              varTokensToDisplayValue={varTokensToDisplayValue}
              setConfig={(newConfig) => {
                const newVariables = Object.assign([], variables, {
                  [i]: {
                    ...variable,
                    ...newConfig,
                  },
                });
                setVariables(newVariables);
              }}
              onDelete={() => deleteVariable(i)}
            />
          </Well>
        );
      })}
    </div>
  );
};

export default Editor;
